import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type CatalogueSearchRow,
  type ImportRow,
  type ImportSummary,
  type Sector,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAddBatch, useCreateListing, useSellerPharmacy } from '@/lib/onboardingQueries';
import { Icon } from '@/components/icons/Icon';
import { BarcodeScanner } from '@/components/ui/BarcodeScanner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';

type Mode = 'scan' | 'search' | 'import';

/** A product chosen to list, from a scan or a search. */
interface Picked {
  id: number;
  display_name: string;
  is_pom?: boolean;
  is_veterinary?: boolean;
}

/**
 * The marker on a catalogue row that is not a human medicine.
 *
 * Only a seller approved for BOTH ever sees one — the API does not return the
 * other side's products at all — but for that seller it is the difference
 * between pricing a cattle antibiotic and pricing the human one with a name
 * three characters different.
 */
function AnimalTag() {
  return (
    <span className="rounded-pill border border-blue px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue">
      Animal
    </span>
  );
}

/**
 * Putting stock on ChiCare.
 *
 * ── THREE DOORS, BECAUSE PHARMACIES ARRIVE DIFFERENTLY ──────────────────────
 *
 * SCAN    the counter case. A pharmacist holding a box points the camera at it
 *         and the pack identifies itself — no thirteen digits typed, nothing
 *         mistyped.
 *
 * SEARCH  the "I know what it is called" case, over the WHOLE catalogue rather
 *         than the buyer-facing one. The buyer search hides products nobody
 *         stocks yet, which would strand the first pharmacy to carry one.
 *
 * IMPORT  the joining case. A pharmacy with six hundred lines will not type
 *         them one at a time, and nearly all of them already have the list in
 *         a POS export or a spreadsheet.
 *
 * ── AND ONE DOOR OUT ────────────────────────────────────────────────────────
 *
 * When the catalogue genuinely does not have a pack, the pharmacy describes it
 * and photographs it and ChiCare's team adds it. They never write drug facts
 * themselves (§9.1) — a pharmacy that could type its own NAFDAC number could
 * type any, and the authenticity promise rests on that number being ours.
 */
export function AddProductScreen() {
  const [mode, setMode] = useState<Mode>('scan');
  const [picked, setPicked] = useState<Picked | null>(null);
  const [proposing, setProposing] = useState<Record<string, string> | null>(null);

  if (picked) {
    return <ListItPanel product={picked} onDone={() => setPicked(null)} />;
  }

  if (proposing) {
    return <ProposeForm prefill={proposing} onCancel={() => setProposing(null)} />;
  }

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Add stock</h1>
        <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          Scan a pack, search the catalogue, or upload your whole stock list. Anything we do not
          have yet, tell us about and our team adds it — usually within a working day.
        </p>
      </header>

      <div className="mb-5 flex flex-wrap gap-1.5">
        {(
          [
            ['scan', 'Scan a pack'],
            ['search', 'Search'],
            ['import', 'Upload a file'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-semibold',
              mode === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'scan' ? <ScanPanel onPick={setPicked} onPropose={setProposing} /> : null}
      {mode === 'search' ? <SearchPanel onPick={setPicked} onPropose={setProposing} /> : null}
      {mode === 'import' ? <ImportPanel onPropose={setProposing} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scan
// ---------------------------------------------------------------------------

function ScanPanel({
  onPick,
  onPropose,
}: {
  onPick: (product: Picked) => void;
  onPropose: (prefill: Record<string, string>) => void;
}) {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (scanned: string) => api.catalogueLookup(scanned),
    onSuccess: (result) => {
      if (result.found && result.product && !result.already_listed) {
        onPick(result.product);
      }
    },
  });

  if (scanning) {
    return (
      <BarcodeScanner
        onDetected={(scanned) => {
          setScanning(false);
          setCode(scanned);
          lookup.mutate(scanned);
        }}
        onCancel={() => setScanning(false)}
      />
    );
  }

  const result = lookup.data;

  return (
    <div className="space-y-4">
      <Card className="!p-5">
        <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
          Point your camera at the pack
        </h2>
        <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
          The barcode identifies the medicine on its own. You only add your price, the batch number
          and the expiry date.
        </p>

        <Button className="mt-4" onClick={() => setScanning(true)}>
          Open the camera
        </Button>
      </Card>

      {lookup.isPending ? <Skeleton className="h-20 w-full" radius="card" /> : null}

      {result && !lookup.isPending ? (
        <Card className="!p-5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {code}
          </p>

          {result.found && result.already_listed ? (
            <>
              <h3 className="m-0 mt-2 text-[15px] font-bold text-ink">
                You already list {result.product?.display_name}
              </h3>
              <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
                Add a new batch to it rather than listing it twice.
              </p>
              <Button variant="grey" className="mt-3" onClick={() => navigate('/seller/listings')}>
                Open your listings
              </Button>
            </>
          ) : result.found ? (
            <h3 className="m-0 mt-2 text-[15px] font-bold text-ink">
              {result.product?.display_name}
            </h3>
          ) : (
            <>
              <h3 className="m-0 mt-2 text-[15px] font-bold text-ink">We do not have this pack</h3>
              <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
                {result.message}
              </p>
              <Button className="mt-3" onClick={() => onPropose({ gtin: code ?? '' })}>
                Tell us about it
              </Button>
            </>
          )}
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function SearchPanel({
  onPick,
  onPropose,
}: {
  onPick: (product: Picked) => void;
  onPropose: (prefill: Record<string, string>) => void;
}) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(term.trim()), 300);

    return () => window.clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['seller', 'catalogue-search', query],
    queryFn: ({ signal }) => api.sellerCatalogueSearch(query, signal),
    enabled: query.length >= 2,
  });

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          Search the ChiCare catalogue
        </span>
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          autoFocus
          placeholder="Paracetamol, Ventolin, a barcode…"
          className="min-h-12 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        />
      </label>

      {results.isLoading && query.length >= 2 ? <Skeleton className="h-20 w-full" radius="card" /> : null}

      {results.data && results.data.length > 0 ? (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {results.data.map((product: CatalogueSearchRow) => (
            <div
              key={product.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-2 px-4 py-3 last:border-b-0"
            >
              <div className="min-w-[180px] flex-1">
                <p className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-semibold text-ink">
                  {product.is_veterinary ? <AnimalTag /> : null}
                  {product.display_name}
                </p>
                <p className="m-0 mt-0.5 text-[12px] text-muted">
                  {[product.form, product.pack_size, product.manufacturer].filter(Boolean).join(' · ')}
                </p>
              </div>

              {product.already_listed ? (
                <Button size="sm" variant="grey" onClick={() => navigate('/seller/listings')}>
                  Already listed
                </Button>
              ) : (
                <Button size="sm" onClick={() => onPick(product)}>
                  List this
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {query.length >= 2 && results.data?.length === 0 && !results.isLoading ? (
        <Card className="!p-5">
          <h3 className="m-0 text-[15px] font-bold text-ink">Nothing matched “{query}”</h3>
          <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
            ChiCare curates the catalogue centrally so every pharmacy sells against the same drug
            facts. Tell us about the pack and our team adds it — then anyone can list it.
          </p>
          <Button className="mt-3" onClick={() => onPropose({ generic_name: query })}>
            Tell us about it
          </Button>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List it — price, batch, expiry
// ---------------------------------------------------------------------------

function ListItPanel({ product, onDone }: { product: Picked; onDone: () => void }) {
  const navigate = useNavigate();
  const createListing = useCreateListing();
  const addBatch = useAddBatch();

  const [naira, setNaira] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [expiry, setExpiry] = useState('');
  const [quantity, setQuantity] = useState('');

  const priceKobo = Math.round(Number(naira || 0) * 100);
  const ready = priceKobo >= 100 && batchNo.trim() !== '' && expiry !== '' && Number(quantity) > 0;

  const save = useMutation({
    mutationFn: async () => {
      const listing = await createListing.mutateAsync({ productId: product.id, priceKobo });

      await addBatch.mutateAsync({
        listingId: listing.id,
        batchNo: batchNo.trim(),
        expiryDate: expiry,
        quantity: Number(quantity),
      });
    },
    onSuccess: () => navigate('/seller/listings'),
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.4} />
        Choose something else
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        {product.display_name}
      </h1>
      <p className="m-0 mt-1 text-[13px] text-muted">
        The drug facts come from the catalogue. These four are yours.
      </p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) save.mutate();
        }}
      >
        <Field
          label="Your price"
          hint="What you receive. ChiCare's share is added on top for the buyer."
          value={naira}
          onChange={setNaira}
          inputMode="decimal"
          prefix="₦"
        />
        <Field
          label="Batch number"
          hint="From the pack you are putting on the shelf."
          value={batchNo}
          onChange={setBatchNo}
        />
        <Field label="Expiry date" value={expiry} onChange={setExpiry} type="date" />
        <Field label="How many" value={quantity} onChange={setQuantity} inputMode="numeric" />

        {error ? (
          <p role="alert" className="m-0 text-[12.5px] font-medium leading-relaxed text-danger">
            {error.message}
          </p>
        ) : null}

        <Button full type="submit" loading={save.isPending} disabled={!ready}>
          Put it on sale
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Propose — the door out of the catalogue
// ---------------------------------------------------------------------------

/** Typed rather than a loose record, so a renamed field is a compile error. */
interface ProposeFields {
  generic_name: string;
  brand_name: string;
  strength: string;
  form: string;
  pack_size: string;
  manufacturer: string;
  nafdac_reg_no: string;
  gtin: string;
  price: string;
  batch_no: string;
  expiry_date: string;
  quantity: string;
}

function ProposeForm({
  prefill,
  onCancel,
}: {
  prefill: Record<string, string>;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  // What this seller is approved for. A shop in one sector never has to say
  // which one — the API fills it in. A shop in both must, because a curator
  // reading a photograph of a carton cannot always tell.
  const { data: registration } = useSellerPharmacy();
  const sectors = registration?.pharmacy?.sectors ?? ['human'];
  const mustChooseSector = sectors.length > 1;

  const [sector, setSector] = useState<Sector>(sectors[0] ?? 'human');

  const [fields, setFields] = useState<ProposeFields>({
    generic_name: '',
    brand_name: '',
    strength: '',
    form: '',
    pack_size: '',
    manufacturer: '',
    nafdac_reg_no: '',
    gtin: '',
    price: '',
    batch_no: '',
    expiry_date: '',
    quantity: '',
    ...prefill,
  });

  const [photos, setPhotos] = useState<Array<{ preview: string; path: string | null }>>([]);
  const [done, setDone] = useState<string | null>(null);

  const set = (key: keyof ProposeFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));

  async function addPhotos(files: FileList | null) {
    for (const file of Array.from(files ?? []).slice(0, 4 - photos.length)) {
      const preview = URL.createObjectURL(file);
      setPhotos((current) => [...current, { preview, path: null }]);

      try {
        const { path } = await api.uploadCataloguePhoto(file);
        setPhotos((current) => current.map((p) => (p.preview === preview ? { ...p, path } : p)));
      } catch {
        setPhotos((current) => current.filter((p) => p.preview !== preview));
      }
    }
  }

  const submit = useMutation({
    mutationFn: () =>
      api.submitProduct({
        ...fields,
        // Sent only where the seller had a genuine choice. Otherwise the server
        // fills it from what they are approved for, which cannot be wrong.
        sector: mustChooseSector ? sector : undefined,
        price_kobo: Math.round(Number(fields.price || 0) * 100),
        quantity: Number(fields.quantity || 0),
        photos: photos.map((p) => p.path).filter(Boolean),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['seller', 'submissions'] });
      setDone(result.message);
    },
  });

  const uploaded = photos.filter((p) => p.path).length;
  const ready =
    fields.generic_name.trim() !== '' &&
    Number(fields.price) > 0 &&
    fields.batch_no.trim() !== '' &&
    fields.expiry_date !== '' &&
    Number(fields.quantity) > 0 &&
    uploaded > 0;

  const fieldErrors = submit.error instanceof ApiError ? submit.error.fieldErrors : {};

  if (done) {
    return (
      <div className="mx-auto w-full max-w-[560px] px-4 py-6 xl:px-8">
        <Card className="!p-5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">Sent</p>
          <h1 className="m-0 mt-2 font-display text-[21px] font-bold tracking-[-0.02em]">
            With our catalogue team
          </h1>
          <p className="m-0 mt-2 text-[14px] leading-relaxed text-ink">{done}</p>
          <Button className="mt-5" onClick={onCancel}>
            Add something else
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onCancel}
        className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.4} />
        Back
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Tell us about the pack
      </h1>
      <p className="m-0 mt-1 max-w-[58ch] text-[13px] leading-relaxed text-muted">
        Our team checks it against the NAFDAC register and adds it, usually within a working day.
        Once it is in, every pharmacy can list it — and you were first.
      </p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) submit.mutate();
        }}
      >
        {/* Asked FIRST, and only of a shop that sells both. It decides which
            catalogue this becomes part of, and a curator looking at a
            photograph of a carton often cannot tell — plenty of veterinary
            packs carry the same active ingredient and a similar layout. */}
        {mustChooseSector ? (
          <section className="rounded-card border border-line p-4">
            <h2 className="m-0 text-[14px] font-bold text-ink">Is this for people or animals?</h2>
            <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-muted">
              You are registered for both, so we need you to say. It decides which shelf buyers
              find it on once we add it.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {([
                ['human', 'For people'],
                ['veterinary', 'For animals'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={sector === key}
                  onClick={() => setSector(key)}
                  className={cn(
                    'min-h-11 rounded-btn border px-3.5 text-[13px] font-semibold',
                    sector === key
                      ? key === 'veterinary'
                        ? 'border-blue bg-blue text-white'
                        : 'border-blue bg-blue text-white'
                      : 'border-line bg-white text-muted hover:text-ink',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        ) : null}

        <Field label={sector === 'veterinary' ? 'Product name' : 'Medicine name'} value={fields.generic_name} onChange={set('generic_name')} error={fieldErrors.generic_name?.[0]} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Brand" value={fields.brand_name} onChange={set('brand_name')} />
          <Field label="Strength" value={fields.strength} onChange={set('strength')} />
          <Field label="Form" hint="Tablet, syrup, inhaler…" value={fields.form} onChange={set('form')} />
          <Field label="Pack size" value={fields.pack_size} onChange={set('pack_size')} />
          <Field label="Manufacturer" value={fields.manufacturer} onChange={set('manufacturer')} />
          <Field label="NAFDAC number" value={fields.nafdac_reg_no} onChange={set('nafdac_reg_no')} />
        </div>
        <Field label="Barcode" hint="The number under the stripes, if the pack has one." value={fields.gtin} onChange={set('gtin')} />

        <section>
          <h2 className="m-0 text-[14px] font-bold text-ink">Photos of the pack</h2>
          <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-muted">
            At least one, with the NAFDAC number readable. It is what our team checks.
          </p>

          {photos.length > 0 ? (
            <ul className="m-0 mt-3 grid list-none grid-cols-4 gap-2 p-0">
              {photos.map((photo) => (
                <li
                  key={photo.preview}
                  data-testid="pack-photo"
                  className="relative aspect-square overflow-hidden rounded-[10px] border border-line bg-paper"
                >
                  <img src={photo.preview} alt="" className="h-full w-full object-cover" />
                  {!photo.path ? (
                    <span className="absolute inset-0 grid place-items-center bg-white/70 text-[11px] font-semibold text-muted">
                      Uploading…
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            data-testid="pack-photo-input"
            onChange={(event) => {
              void addPhotos(event.target.files);
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="grey"
            className="mt-3"
            disabled={photos.length >= 4}
            onClick={() => fileInput.current?.click()}
          >
            {photos.length === 0 ? 'Add a photo' : 'Add another'}
          </Button>
          {fieldErrors.photos?.[0] ? (
            <p role="alert" className="m-0 mt-2 text-[12px] font-medium text-danger">
              {fieldErrors.photos[0]}
            </p>
          ) : null}
        </section>

        <section>
          <h2 className="m-0 text-[14px] font-bold text-ink">What you are selling</h2>
          <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-muted">
            Captured now so you go on sale the moment it is approved, rather than coming back.
          </p>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field label="Your price" value={fields.price} onChange={set('price')} inputMode="decimal" prefix="₦" />
            <Field label="How many" value={fields.quantity} onChange={set('quantity')} inputMode="numeric" />
            <Field label="Batch number" value={fields.batch_no} onChange={set('batch_no')} />
            <Field label="Expiry date" value={fields.expiry_date} onChange={set('expiry_date')} type="date" />
          </div>
        </section>

        {submit.error && !(submit.error instanceof ApiError && submit.error.isValidation) ? (
          <p role="alert" className="m-0 text-[12.5px] font-medium text-danger">
            {submit.error instanceof ApiError ? submit.error.message : 'That did not send. Try again.'}
          </p>
        ) : null}

        <Button full type="submit" loading={submit.isPending} disabled={!ready}>
          Send it to ChiCare
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

function ImportPanel({ onPropose }: { onPropose: (prefill: Record<string, string>) => void }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ rows: ImportRow[]; summary: ImportSummary; dryRun: boolean } | null>(null);

  const run = useMutation({
    mutationFn: ({ chosen, dryRun }: { chosen: File; dryRun: boolean }) =>
      api.importListings(chosen, dryRun),
    onSuccess: (data) => {
      setResult(data);
      if (!data.dryRun) void queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] });
    },
  });

  const error = run.error instanceof ApiError ? run.error : null;

  return (
    <div className="space-y-4">
      <CatalogueSheetPanel />

      <Card className="!p-5">
        <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
          Upload your stock list
        </h2>
        <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
          A CSV from your POS or a spreadsheet. We read the columns as they are — barcode, name,
          price, batch, expiry, quantity — whatever you happen to call them. Rows with a barcode or
          NAFDAC number go on sale straight away; the rest come back for our team to add.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="hidden"
          data-testid="import-input"
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null;
            setFile(chosen);
            setResult(null);
            if (chosen) run.mutate({ chosen, dryRun: true });
            event.target.value = '';
          }}
        />

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant={file ? 'grey' : 'primary'} onClick={() => fileInput.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a CSV file'}
          </Button>
          {file ? <span className="text-[12.5px] text-muted">{file.name}</span> : null}
        </div>

        {error ? (
          <p role="alert" className="m-0 mt-3 text-[12.5px] font-medium text-danger">
            {error.message}
          </p>
        ) : null}
      </Card>

      {run.isPending ? <Skeleton className="h-24 w-full" radius="card" /> : null}

      {result ? (
        <Card className="!p-5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {result.dryRun ? 'Preview — nothing saved yet' : 'Done'}
          </p>

          <dl className="m-0 mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-4">
            <Stat label="Rows" value={result.summary.total} />
            <Stat label="On sale" value={result.summary.listed} />
            <Stat label="Restocked" value={result.summary.restocked} />
            <Stat label="For our team" value={result.summary.needs_review} />
          </dl>

          {result.summary.errors > 0 ? (
            <p className="m-0 mt-3 text-[13px] text-danger">
              {result.summary.errors} row{result.summary.errors === 1 ? '' : 's'} could not be read —
              see below.
            </p>
          ) : null}

          {result.dryRun && file ? (
            <Button
              className="mt-4"
              loading={run.isPending}
              onClick={() => run.mutate({ chosen: file, dryRun: false })}
            >
              Apply this
            </Button>
          ) : null}

          <ul className="m-0 mt-4 max-h-[360px] list-none space-y-1.5 overflow-y-auto p-0">
            {result.rows.map((row) => (
              <li
                key={row.line}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-line-2 pb-1.5 text-[12.5px] last:border-b-0"
              >
                <span className="font-mono text-[11px] text-faint">L{row.line}</span>
                <span className="min-w-0 flex-1 font-medium text-ink">{row.product ?? row.name}</span>
                <span
                  className={cn(
                    'font-mono text-[10px] uppercase tracking-[0.1em]',
                    row.outcome === 'error' && 'text-danger',
                    row.outcome === 'needs_review' && 'text-caution',
                    (row.outcome === 'listed' || row.outcome === 'restocked') && 'text-verified',
                  )}
                >
                  {row.outcome.replace('_', ' ')}
                </span>
                <span className="w-full text-[12px] text-muted">{row.message}</span>

                {row.outcome === 'needs_review' && !result.dryRun ? (
                  <button
                    type="button"
                    onClick={() =>
                      onPropose({
                        ...(row.proposal ?? {}),
                        price: row.price_kobo ? String(row.price_kobo / 100) : '',
                        batch_no: row.batch_no ?? '',
                        expiry_date: row.expiry_date ?? '',
                        quantity: row.quantity ? String(row.quantity) : '',
                      })
                    }
                    className="min-h-9 text-[12px] font-bold text-blue"
                  >
                    Send this one to ChiCare
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="m-0 text-[11.5px] text-faint">{label}</dt>
      <dd className="m-0 font-mono text-[17px] font-bold tabular-nums text-ink">{value}</dd>
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  inputMode,
  prefix,
  error,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
  prefix?: string;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12.5px] font-medium text-ink">{label}</span>
      {hint ? <span className="mb-1.5 block text-[11.5px] leading-relaxed text-muted">{hint}</span> : null}

      <span className="flex items-center gap-1.5 rounded-btn border border-line bg-white px-3 focus-within:border-blue">
        {prefix ? <span className="text-[14px] text-faint">{prefix}</span> : null}
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-12 w-full min-w-0 border-0 bg-transparent text-[14px] text-ink outline-none"
        />
      </span>

      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : null}
    </label>
  );
}


/**
 * Download the catalogue, price what you stock, send it back.
 *
 * ── WHY THIS COMES FIRST ────────────────────────────────────────────────────
 *
 * The uploader below has always been forgiving about column names, because a
 * stock file comes out of whatever POS the shop owns. That solves the wrong
 * half: the pharmacy still types drug names and ChiCare still guesses which
 * product each row means. Six hundred rows of guesses is six hundred chances
 * to list the wrong strength.
 *
 * Downloading first inverts it. The seller sees the exact products ChiCare
 * carries, fills in the four things only they know, and sends back a sheet
 * carrying an id that removes the guessing entirely. They priced what they
 * could SEE.
 */
function CatalogueSheetPanel() {
  // Null until the seller picks one. The DEFAULT comes from what they are
  // approved for, not from a hardcoded 'human' — a vet-only practice has no
  // sector chips to press, so a hardcoded default silently asked for the one
  // shelf they are not allowed to see and got refused.
  const [chosen, setChosen] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string>('');

  const options = useQuery({
    queryKey: ['seller', 'catalogue-sheet'],
    queryFn: ({ signal }) => api.catalogueSheetOptions(signal),
  });

  const download = useMutation({
    mutationFn: () => api.catalogueSheet(sector, categoryId ? Number(categoryId) : null),
    onSuccess: (blob) => {
      // Saved here rather than in the client, so the client stays free of the
      // DOM and the mobile app can use the same method.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const shelf = categoryId
        ? options.data?.categories.find((one) => String(one.id) === categoryId)?.name
        : 'all';

      link.href = url;
      link.download = `chicare-${sector === 'veterinary' ? 'veterinary' : 'pharmacy'}-${
        shelf ? shelf.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'all'
      }.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

  const error = download.error instanceof ApiError ? download.error : null;
  const sectors = options.data?.sectors ?? [];
  const sector = chosen ?? sectors[0]?.value ?? 'human';
  const shelves = (options.data?.categories ?? []).filter((one) => one.sector === sector);

  return (
    <Card className="!p-5">
      <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
        Start from our catalogue
      </h2>
      <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
        Download the drugs we carry — names, strengths, pack sizes, manufacturers and NAFDAC
        numbers already filled in. Add your price, quantity, batch number and expiry, then upload it
        below. Because you picked from our list, nothing has to be guessed.
      </p>

      {sectors.length > 1 ? (
        <div className="mt-3.5 flex flex-wrap gap-1.5">
          {sectors.map((one) => (
            <button
              key={one.value}
              type="button"
              data-testid={`sheet-sector-${one.value}`}
              onClick={() => {
                setChosen(one.value);
                setCategoryId('');
              }}
              className={cn(
                'min-h-9 rounded-full border px-3.5 text-[12.5px] font-semibold',
                sector === one.value
                  ? 'border-blue bg-blue text-white'
                  : 'border-line bg-white text-muted hover:text-ink',
              )}
            >
              {one.label}
            </button>
          ))}
        </div>
      ) : null}

      <label className="mt-3.5 block">
        <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Which shelf?</span>
        <select
          value={categoryId}
          data-testid="sheet-category"
          onChange={(event) => setCategoryId(event.target.value)}
          className="min-h-12 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        >
          <option value="">Everything we carry</option>
          {shelves.map((one) => (
            <option key={one.id} value={one.id}>
              {one.name} ({one.products})
            </option>
          ))}
        </select>
      </label>

      {error ? (
        <p role="alert" className="m-0 mt-2.5 text-[12.5px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <Button
        className="mt-3.5"
        disabled={download.isPending || options.isLoading}
        onClick={() => download.mutate()}
      >
        {download.isPending ? 'Preparing…' : 'Download the sheet'}
      </Button>

      <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-faint">
        Built fresh every time you press it, so anything we added this week is already in it. The
        four columns to fill are price, quantity, batch_no and expiry_date — manufactured_on is
        optional and helps if a batch is ever recalled by production date.
      </p>
    </Card>
  );
}
